import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/* Page-level SEO: title / description / canonical / Open Graph / Twitter card /
   optional JSON-LD, plus an auto-built BreadcrumbList. Modelled on the
   ElevateStaffing SEO component. Every public page renders one of these so each
   route has its own crawlable, self-canonical metadata instead of sharing the
   generic homepage tags from index.html. */

export const SITE_NAME = "CareerSavvy";
export const BASE_URL = "https://careersavvy.ai";
const DEFAULT_IMAGE = `${BASE_URL}/og-card.png`;
/* Keep descriptions under ~155 characters — Google truncates around there, and the
   Aug 2026 SEO audit flagged this one for length. */
const DEFAULT_DESC =
  "CareerSavvy is your AI-powered job-search assistant: personalised job matches, AI resume tailoring, mock interviews and interview prep. Start free.";

export default function SEO({
  title,
  description,
  canonicalPath,
  ogType = "website",
  ogImage,
  jsonLd,
  breadcrumbs,
  noIndex,
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Your AI-Powered Job Search Assistant`;
  const desc = description || DEFAULT_DESC;
  const canonicalUrl = canonicalPath ? `${BASE_URL}${canonicalPath}` : undefined;
  const finalOgImage = ogImage || DEFAULT_IMAGE;

  /* index.html ships a baseline set of meta tags so crawlers that do not execute JS
     still get a title, description and canonical. Helmet only manages the tags it
     created (it marks them data-rh), so those static ones survive alongside ours —
     every route was serving TWO descriptions and TWO og:titles, and a search engine
     shown two conflicting values for a tag may honour neither. Drop the copies
     Helmet does not own, once it has rendered ours.

     Stripped unconditionally because SEO.jsx always emits a replacement for each of
     these; canonical and og:url are only emitted when we have a canonical URL, so
     those are only stripped when we actually have one to put back. */
  useEffect(() => {
    const always = [
      'meta[name="description"]',
      'meta[name="robots"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:type"]',
      'meta[property="og:site_name"]',
      'meta[property="og:image"]',
      'meta[name="twitter:card"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
      'meta[name="twitter:image"]',
    ];
    const conditional = ['link[rel="canonical"]', 'meta[property="og:url"]'];
    const selectors = canonicalUrl ? always.concat(conditional) : always;
    selectors.forEach((sel) => {
      document.head
        .querySelectorAll(`${sel}:not([data-rh])`)
        .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    });
  }, [canonicalUrl, fullTitle, desc]);

  let breadcrumbJsonLd = null;
  if (Array.isArray(breadcrumbs) && breadcrumbs.length) {
    const items = [{ label: "Home", path: "/" }, ...breadcrumbs];
    breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.label,
        item: `${BASE_URL}${b.path}`,
      })),
    };
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noIndex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={finalOgImage} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={finalOgImage} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      )}
    </Helmet>
  );
}
